import type { TenantModuleKey } from "@/hooks/useTenantCapabilities";
import { TENANT_MODULE_KEYS } from "@/hooks/useTenantCapabilities";

/**
 * Presentation metadata for tenant module entitlements (V2 admin UI only).
 * Enforcement is still deferred — nothing operational reads these.
 */
export interface TenantModuleMeta {
  key: TenantModuleKey;
  label: string;
  scope: string;
}

export const TENANT_MODULE_META: TenantModuleMeta[] = [
  {
    key: "factory_portal",
    label: "Factory Portal",
    scope: "Factory staff login, order processing, stations and scanning.",
  },
  {
    key: "outlet_portal",
    label: "Outlet Portal",
    scope: "Outlet staff login, order intake, returns and collection.",
  },
  {
    key: "logistics",
    label: "Logistics",
    scope: "Driver Portal, Tasks, Route Management, Logistics Requests.",
  },
];

export const TENANT_MODULE_DEFERRED_NOTICE =
  "Saved settings now hide this module's pages and navigation in the app. Server, API and database enforcement is still deferred, so existing operations are not interrupted.";

export function getTenantModuleMeta(key: string): TenantModuleMeta | undefined {
  return TENANT_MODULE_META.find((m) => m.key === key);
}

export function getTenantModuleLabel(key: string): string {
  return getTenantModuleMeta(key)?.label ?? key;
}

/** Confirmation copy shown only when a module is being turned OFF. */
export function getDisableConfirmationMessage(key: string): string {
  return `Disabling ${getTenantModuleLabel(key)} hides its pages and navigation for this tenant in the app. It does not yet restrict server, API or database access, and does not stop any running operations.`;
}

/** Confirmation is required only when switching a module off. */
export function requiresConfirmation(nextEnabled: boolean): boolean {
  return nextEnabled === false;
}

/** Guard: the admin UI must only ever present the three V1 module keys. */
export function isSupportedModuleKey(key: string): boolean {
  return (TENANT_MODULE_KEYS as readonly string[]).includes(key);
}
